import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingLayout from 'pages/booking/BookingLayout';
import { BOOKING_ACCOUNT_CREDIT_PURCHASE_DETAIL_PATH, BOOKING_DETAIL_PATH } from 'routes/paths';
import useLang from 'lang';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';
import 'pages/booking/credits/credit-purchase.css';

/** Creates a stable checkout request id for API idempotency. */
function createRequestId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
      const random = Math.floor(Math.random() * 16);
      return (value === 'x' ? random : (random & 0x3) | 0x8).toString(16);
    })
  );
}

/** Checkout for a prepaid credit bundle. */
export default function CreditPurchaseCheckout() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAction } = useApi();
  const { _t, _tn } = useLang();
  const routedSpace = (location.state as any)?.space ?? null;
  const routedSpaceId = Number(routedSpace?.id) || 0;
  const [space, setSpace] = useState<any>(routedSpace);
  const [selectedProductId, setSelectedProductId] = useState(Number(searchParams.get('productId')) || 0);
  const [street, setStreet] = useState<string>(space?.billingAddress?.street ?? '');
  const [number, setNumber] = useState<string>(space?.billingAddress?.number ?? '');
  const [city, setCity] = useState<string>(space?.billingAddress?.city ?? '');
  const [zip, setZip] = useState<string>(space?.billingAddress?.zip ?? '');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useMemo(createRequestId, []);

  useEffect(() => {
    let cancelled = false;
    const spaceIdPromise = routedSpaceId
      ? Promise.resolve(routedSpaceId)
      : getAction('GetPublicReservableSpace')({ parameters: { slug } }).then((data: any) => data.space.id);
    spaceIdPromise
      .then((spaceId) => getAction('GetReservableSpace')({ parameters: { id: spaceId } }))
      .then((data: any) => {
        if (cancelled) return;
        setSpace(data.space);
        setSelectedProductId((current) => current || data.space.creditProducts?.[0]?.id || 0);
        setStreet((current) => current || data.space.billingAddress?.street || '');
        setNumber((current) => current || data.space.billingAddress?.number || '');
        setCity((current) => current || data.space.billingAddress?.city || '');
        setZip((current) => current || data.space.billingAddress?.zip || '');
      })
      .catch(() => {
        if (cancelled) return;
        setSpace(null);
        setError(_t('Credit bundles are currently unavailable.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getAction, routedSpaceId, slug]);

  const products = space?.creditProducts ?? [];
  const selected = products.find((product: any) => Number(product.id) === selectedProductId);
  const valid = Boolean(selected && street.trim() && number.trim() && city.trim() && zip.trim() && accepted);

  /** Creates the purchase and continues to Stripe or its local detail page. */
  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const response: any = await getAction('CreateReservationCreditPurchase')({
        payload: {
          productId: selectedProductId,
          requestId,
          billing: {
            street: street.trim(),
            number: number.trim(),
            city: city.trim(),
            zip: zip.trim(),
            countryCode: 'SK',
          },
        },
      });
      if (response.purchase.attempt?.checkoutUrl) {
        window.location.assign(response.purchase.attempt.checkoutUrl);
        return;
      }
      navigate(BOOKING_ACCOUNT_CREDIT_PURCHASE_DETAIL_PATH.insert({ purchaseId: String(response.purchase.id) }), {
        replace: true,
        state: { purchase: response.purchase },
      });
    } catch (caught: any) {
      setError(caught.response?.data?.info || caught.response?.data?.msg || _t('The purchase could not be created.'));
      setSubmitting(false);
    }
  };

  return (
    <BookingLayout>
      <main className="credit-purchase-page">
        <button className="back-btn" onClick={() => navigate(BOOKING_DETAIL_PATH.insert({ slug }))}>
          <img src={IconArrowLeft} width={20} height={20} alt="" />
          {_t('Back to space')}
        </button>
        <h1>{_t('Buy credits')}</h1>
        {loading && <p>{_t('Loading...')}</p>}
        {!loading && products.length === 0 && <p>{_t('No credit bundles are currently available.')}</p>}
        {products.length > 0 && (
          <>
            <h2>{space.name}</h2>
            <div className="credit-product-grid">
              {products.map((product: any) => (
                <button
                  className={`credit-product${selectedProductId === product.id ? ' credit-product--selected' : ''}`}
                  key={product.id}
                  onClick={() => setSelectedProductId(product.id)}
                >
                  <strong>
                    {_tn('%d credit', '%d credits', Number(product.credits)).replace('%d', String(product.credits))}
                  </strong>
                  <span>
                    {Number(product.priceWithVat).toFixed(2)} {product.currencyCode}
                  </span>
                  <small>{_t('Expires %d days after payment').replace('%d', String(product.expiresInDays))}</small>
                </button>
              ))}
            </div>
            <section className="credit-purchase-card">
              <h2>{_t('Billing address')}</h2>
              <div className="credit-billing-grid">
                <label>
                  <span>{_t('Street')}</span>
                  <input value={street} onChange={(event) => setStreet(event.target.value)} />
                </label>
                <label>
                  <span>{_t('Number')}</span>
                  <input value={number} onChange={(event) => setNumber(event.target.value)} />
                </label>
                <label>
                  <span>{_t('City')}</span>
                  <input value={city} onChange={(event) => setCity(event.target.value)} />
                </label>
                <label>
                  <span>{_t('ZIP code')}</span>
                  <input value={zip} onChange={(event) => setZip(event.target.value)} />
                </label>
              </div>
              <label className="credit-purchase-consent">
                <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                <span>
                  {_t('I agree to the purchase terms and understand that credits expire after the stated period.')}
                </span>
              </label>
              {selected && (
                <p className="credit-purchase-total">
                  {_t('Total including VAT')}:{' '}
                  <strong>
                    {Number(selected.priceWithVat).toFixed(2)} {selected.currencyCode}
                  </strong>
                </p>
              )}
              {error && (
                <p className="credit-purchase-error" role="alert">
                  {error}
                </p>
              )}
              <button className="cta-btn" disabled={!valid || submitting} onClick={() => void submit()}>
                {submitting ? _t('Processing...') : _t('Buy with obligation to pay')}
              </button>
            </section>
          </>
        )}
      </main>
    </BookingLayout>
  );
}
